import React from 'react';
import { NSLabel, NSModal } from 'bricks';
import { IReportBuilderReport } from 'views/ReportBuilder/constants.js';
import { useParams } from 'react-router-dom';
import { downloadFileFromUrl } from 'helpers/file/download';
import { useCreateCombinedReportPdf } from 'views/ReportBuilder/hooks/useCreateCombinedReportPdf';
import useToast, { ToastType } from 'hooks/useToast';
import ReportListItem from './ReportListItem';

export interface IGenerateReportModalProps {
    orgId: string;
    newReports: Array<IReportBuilderReport>;
    isOpen: boolean;
    toggle: () => void;
}

const GenerateReportModal = ({
    orgId, newReports, toggle, isOpen,
}: IGenerateReportModalProps) => {
    const { dealId: dealIdString } = useParams<{ dealId: string; proFormaId: string }>();
    const { showToast } = useToast();
    const dealId = Number(dealIdString);

    const { mutateAsync: generateReportPdf, isPending: isGeneratingReport } = useCreateCombinedReportPdf();

    const generateReport = async () => {
        const toastId = showToast('Generating report, please wait...', ToastType.Success, { autoClose: false });
        try {
            await generateReportPdf({
                dealId,
                postData: {
                    orgId,
                    reportBuilderReports: newReports.map(report => ({
                        reportType: report.reportType,
                        metaData: {
                            savedReportId: report.savedReportId,
                            boeScenarioIds: report.selectedBOEScenarioIds,
                            boeScenarioNames: report.selectedBOEScenarioNames,
                            proFormaId: report.selectedProFormaId,
                            proFormaName: report.selectedProFormaName,
                            dealAttributes: report.selectedDealAttributes,
                            includePrintedOnDate: report.includePrintedOnDate,
                            includeTargetReturns: report.includeTargetReturns,
                            selectedDocuments: report.selectedDocuments,
                        },
                    })),
                },
            }, {
                onSuccess: response => {
                    const pdfUrl = response.url;
                    if (pdfUrl) {
                        downloadFileFromUrl(pdfUrl, 'combined-report', 'pdf', true);
                    }
                    showToast(
                        'Report generated successfully!',
                        ToastType.Success,
                        { autoClose: 3000 },
                        toastId,
                    );
                    toggle();
                },
                onError: () => {
                    showToast(
                        'Failed to generate report. Please try again.',
                        ToastType.Error,
                        { autoClose: 3000 },
                        toastId,
                    );
                },
            });
        } catch (error) {
            console.error('Error generating report:', error);
            showToast(
                'Failed to generate report. Please try again.',
                ToastType.Error,
                { autoClose: 3000 },
                toastId,
            );
        }
    };

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderClassName="bg-primary text-white"
            modalHeaderText="Generate Report"
            footerButtonOutline={false}
            modalFooterButtonColor="primary"
            modalFooterButtonText="Generate"
            modalFooterButtonFunction={generateReport}
            modalFooterButtonIsLoading={isGeneratingReport}
        >
            <NSLabel>Are you sure these are the reports you want to generate? </NSLabel>
            {newReports.map((report, index) => (
                <ReportListItem key={report.reportType} report={report} index={index} />
            ))}
        </NSModal>
    );
};

export default GenerateReportModal;
